import {otsBaseApi} from "../../ots-api";
import {OtsDispatch} from "../../ots-store";
import {Shift, ShiftPayload, ShiftSearchCriteria} from "../../../model/shift";
import {driverActivityCacheUpdate, upsertShiftIntoDriverActivity} from "../driver-activity/driver-activity-store";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["Shifts", "DriverActivity"]});

/**
 * Puts a saved shift into every cached day of driver activity it belongs to, moving or removing its rows when
 * an edit changed its times. Names the save did not send back are taken from what was sent.
 */
const mergeSavedShift = async (
  arg: ShiftPayload,
  {dispatch, queryFulfilled, getState}: {dispatch: OtsDispatch, queryFulfilled: Promise<{data: Shift}>, getState: () => unknown},
) => {
  try {
    const {data} = await queryFulfilled;
    const shift = {...removeUndefinedNames(arg), ...data} as Shift;
    driverActivityCacheUpdate(dispatch, getState() as Parameters<typeof driverActivityCacheUpdate>[1], (draft, window) => upsertShiftIntoDriverActivity(draft, shift, window));
  } catch (error) {
    console.debug(error);
  }
};

const removeUndefinedNames = ({personName, personCompanyName, vehicleName, trailerName}: ShiftPayload) =>
  Object.fromEntries(Object.entries({personName, personCompanyName, vehicleName, trailerName}).filter(([, value]) => value != null));

const shiftApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Legacy /shift/search accepts singular regionId and includes every open shift
    // regardless of start date. Pin the window to now to avoid fetching shift history.
    getActiveShiftCandidates: builder.query<Shift[], {regionId: string}>({
      query: ({regionId}) => {
        const now = Date.now();
        return {
          url: "/shift/search", method: "POST",
          body: {regionId, fromDttm: now, toDttm: now, showHoldings: false, showTabletHoldings: false},
        };
      },
      providesTags: [{type: "Shifts", id: "LIST"}],
    }),
    searchShifts: builder.query<Shift[], ShiftSearchCriteria>({
      query: (tsc) => ({
        url: `/shift/search`,
        method: "POST",
        body: tsc
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "Shifts", id} as const)),
            {type: "Shifts", id: "LIST"}
          ];
        } else {
          return [{type: "Shifts", id: "LIST"}];
        }
      }
    }),
    getShift: builder.query<Shift, string>({
      query: (id) => ({
        url: `/shift/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{type: "Shifts", id}]
    }),
    createShift: builder.mutation<Shift, ShiftPayload>({
      query: (shift) => ({
        url: "/shift",
        method: "POST",
        body: shift
      }),
      // The day is patched rather than fetched again; filtered searches have no window to patch, so they refetch.
      invalidatesTags: [
        {type: "Shifts", id: "LIST"},
        {type: "DriverActivity", id: "LIST"},
      ],
      onQueryStarted: mergeSavedShift,
    }),
    updateShift: builder.mutation<Shift, ShiftPayload>({
      query: (shift) => ({
        url: "/shift",
        method: "PUT",
        body: shift,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Shifts", id},
      ],
      onQueryStarted: mergeSavedShift,
    }),
    saveShift: builder.mutation<Shift, ShiftPayload>({
      query: (shift) => ({
        url: "/shift/save",
        method: "POST",
        body: shift
      }),
      invalidatesTags: (result, error, {id}) => {
        if (id) {
          return [
            {type: "Shifts", id: "LIST"},
            {type: "Shifts", id: id},
          ]
        } else {
          return [
            {type: "Shifts", id: "LIST"},
          ]
        }
      },
      onQueryStarted: mergeSavedShift,
    }),
    /** Merges a driver's back-to-back shifts into one. The day is fetched again: the merged shift's rows change. */
    mergeShifts: builder.mutation<Shift, Shift[]>({
      query: (shifts) => ({
        url: "/shift/merge",
        method: "POST",
        body: shifts,
      }),
      invalidatesTags: [{type: "Shifts", id: "LIST"}, {type: "DriverActivity"}],
    }),
    deleteShift: builder.mutation<void, Partial<Shift>>({
      query: (shift) => ({
        url: "/shift",
        method: "DELETE",
        body: shift
      }),
      invalidatesTags: (result, error, {id}) => [{type: "Shifts", id}],
      async onQueryStarted({id}, {dispatch, queryFulfilled, getState}) {
        try {
          await queryFulfilled
          driverActivityCacheUpdate(dispatch, getState(), (draft) => {
            let index = draft.findIndex(s => s.startShift?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }

            index = draft.findIndex(s => s.endShift?.id === id)
            if (index >= 0) {
              draft.splice(index, 1)
            }
          })
        } catch (error) {
          console.debug(error)
        }
      },
    }),
  })
});

const usePrefetch = shiftApi.usePrefetch;

export {usePrefetch as useShiftPrefetch};

export const {
  useGetActiveShiftCandidatesQuery,
  useSearchShiftsQuery,
  useGetShiftQuery,
  useCreateShiftMutation,
  useUpdateShiftMutation,
  useSaveShiftMutation,
  useDeleteShiftMutation,
  useMergeShiftsMutation,
} = shiftApi;
